'use client'

import { cn } from '@brew/ui/cn'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@brew/ui/dialog'
import { useTranslations } from 'next-intl'
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
} from 'react'
import type { SkyPresetName } from '@/components/marketing/home/prompt-showcase/golden-hour-sky'
import {
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { DialogSky } from './dialog-sky'
import { type LucideIcon, XIcon } from './icons'

/**
 * The product dialog kit: the marketing site's look (sky hero with a
 * floating product card, warm ink, orange gradient pills) on Radix Dialog.
 *
 * Anatomy: `BrewDialogContent` › optional `BrewDialogHero` ›
 * `BrewDialogHeader` › `BrewDialogBody` (the only part that scrolls) ›
 * `BrewDialogFooter`. Buttons and inputs inside pick up the skin from
 * `app/dialog-theme.css` through `data-dialog-skin`.
 */

export type BrewDialogSize = 'sm' | 'md' | 'lg' | 'xl'

export const BREW_DIALOG_WIDTH_CLASS: Record<BrewDialogSize, string> = {
  sm: 'sm:max-w-[400px]',
  md: 'sm:max-w-[520px]',
  lg: 'sm:max-w-[680px]',
  xl: 'sm:max-w-[960px]',
}

/**
 * AlertDialog widths carry the primitive's own `data-[size=default]`
 * variant so tailwind-merge replaces its `sm:max-w-lg` instead of the more
 * specific rule winning. Literal strings: Tailwind only sees full classes.
 */
const BREW_ALERT_DIALOG_WIDTH_CLASS: Record<BrewDialogSize, string> = {
  sm: 'data-[size=default]:sm:max-w-[400px]',
  md: 'data-[size=default]:sm:max-w-[520px]',
  lg: 'data-[size=default]:sm:max-w-[680px]',
  xl: 'data-[size=default]:sm:max-w-[960px]',
}

/** Shared surface for every skinned dialog, alert dialog and shell. */
export const BREW_DIALOG_SURFACE_CLASS =
  'flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden rounded-[22px] border-0 bg-[var(--dlg-surface)] p-0 text-[var(--dlg-ink)] shadow-[var(--dlg-shadow)] motion-reduce:animate-none motion-reduce:duration-0 motion-reduce:data-[state=closed]:duration-0 [@media(max-height:599px)]:max-h-[calc(100dvh-1rem)]'

type BrewDialogFrame = { size: BrewDialogSize; hasCloseButton: boolean }

const FrameContext = createContext<BrewDialogFrame>({
  size: 'md',
  hasCloseButton: true,
})

/** Horizontal inset: S dialogs sit a step tighter. */
export function useBrewDialogInset() {
  return useContext(FrameContext).size === 'sm' ? 'px-6' : 'px-7'
}

export const BrewDialog = Dialog
export const BrewDialogClose = DialogClose

/** For surfaces that aren't `BrewDialogContent` (a portaled modal). */
export function BrewDialogSizeProvider({
  size,
  hasCloseButton = true,
  children,
}: {
  size: BrewDialogSize
  hasCloseButton?: boolean
  children: ReactNode
}) {
  return (
    <FrameContext.Provider value={{ size, hasCloseButton }}>
      {children}
    </FrameContext.Provider>
  )
}

export function BrewDialogContent({
  size = 'md',
  className,
  children,
  showCloseButton = true,
  ...props
}: Omit<ComponentProps<typeof DialogContent>, 'showCloseButton'> & {
  size?: BrewDialogSize
  /** Confirms omit it: Cancel and Escape keep the decision explicit. */
  showCloseButton?: boolean
}) {
  return (
    <DialogContent
      data-dialog-skin=""
      data-size={size}
      showCloseButton={false}
      className={cn(
        BREW_DIALOG_SURFACE_CLASS,
        BREW_DIALOG_WIDTH_CLASS[size],
        className
      )}
      {...props}
    >
      <FrameContext.Provider value={{ size, hasCloseButton: showCloseButton }}>
        {children}
        {showCloseButton ? <BrewDialogCloseButton /> : null}
      </FrameContext.Provider>
    </DialogContent>
  )
}

/**
 * AlertDialog surface for confirms: same skin, no close control (Cancel and
 * Escape decide), overlay clicks don't dismiss.
 */
export function BrewAlertDialogContent({
  size = 'sm',
  className,
  children,
  ...props
}: Omit<ComponentProps<typeof AlertDialogContent>, 'size'> & {
  size?: BrewDialogSize
}) {
  return (
    <AlertDialogContent
      data-dialog-skin=""
      className={cn(
        BREW_DIALOG_SURFACE_CLASS,
        BREW_ALERT_DIALOG_WIDTH_CLASS[size],
        className
      )}
      {...props}
    >
      <FrameContext.Provider value={{ size, hasCloseButton: false }}>
        {children}
      </FrameContext.Provider>
    </AlertDialogContent>
  )
}

/**
 * The round 32px control that floats over the sky and over white: the
 * close X top-right, a back arrow top-left. A 48px hit area around it.
 */
export const BREW_DIALOG_ROUND_CONTROL_CLASS =
  "absolute top-4 z-20 flex size-8 touch-manipulation items-center justify-center rounded-full bg-[var(--dlg-surface)]/90 text-[var(--dlg-ink)] shadow-[0_2px_8px_-2px_rgb(120_48_0/0.3)] backdrop-blur transition-colors after:absolute after:-inset-2 after:content-[''] hover:bg-[var(--dlg-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dlg-accent)] disabled:pointer-events-none disabled:opacity-50"

/** The round close control, top-right; reads over the sky and over white. */
export function BrewDialogCloseButton({ className }: { className?: string }) {
  const tCommon = useTranslations('common')
  return (
    <DialogClose
      data-slot="dialog-close"
      className={cn(BREW_DIALOG_ROUND_CONTROL_CLASS, 'right-4', className)}
    >
      <XIcon className="size-4" strokeWidth={2} />
      <span className="sr-only">{tCommon('actions.close')}</span>
    </DialogClose>
  )
}

/** A round control top-left (Back) in the same style as the close X. */
export function BrewDialogBackButton({
  label,
  onClick,
  className,
  children,
}: {
  label: string
  onClick: () => void
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(BREW_DIALOG_ROUND_CONTROL_CLASS, 'left-4', className)}
    >
      {children}
    </button>
  )
}

const HERO_HEIGHT_CLASS: Record<BrewDialogSize, string> = {
  sm: 'h-[168px]',
  md: 'h-[208px]',
  lg: 'h-[224px]',
  xl: 'h-[220px]',
}

/**
 * The top half: a live marketing sky with one floating product card (the
 * thing the dialog is about). Collapses on short viewports so the form
 * stays reachable.
 */
export function BrewDialogHero({
  sky = 'goldenHour',
  children,
  className,
}: {
  sky?: SkyPresetName
  children?: ReactNode
  className?: string
}) {
  const { size } = useContext(FrameContext)
  return (
    <div
      data-slot="brew-dialog-hero"
      aria-hidden="true"
      className={cn(
        'relative isolate flex w-full shrink-0 items-center justify-center overflow-hidden px-6 [@media(max-height:599px)]:hidden',
        HERO_HEIGHT_CLASS[size],
        className
      )}
    >
      <DialogSky preset={sky} />
      <div className="pointer-events-none relative z-10 flex max-w-full items-center justify-center">
        {children}
      </div>
    </div>
  )
}

export type BrewDialogTone = 'default' | 'danger'

const MEDAL_TONE: Record<BrewDialogTone | 'brand', string> = {
  default: 'bg-[var(--dlg-accent-wash)] text-[var(--dlg-accent-deep)]',
  brand: 'bg-[var(--dlg-accent-wash)] text-[var(--dlg-accent-deep)]',
  danger: 'bg-[var(--dlg-danger-wash)] text-[var(--dlg-danger-ink)]',
}

/** The tinted icon tile that leads a confirm without a hero. */
export function BrewDialogMedal({
  icon: Icon,
  tone = 'default',
}: {
  icon: LucideIcon
  tone?: BrewDialogTone
}) {
  return (
    <div
      data-slot="brew-dialog-medal"
      className={cn(
        'flex size-11 shrink-0 items-center justify-center rounded-[14px]',
        MEDAL_TONE[tone]
      )}
    >
      <Icon className="size-5" strokeWidth={2} aria-hidden="true" />
    </div>
  )
}

function PlainTitle(props: ComponentProps<'h2'>) {
  return <h2 {...props} />
}

function PlainDescription(props: ComponentProps<'p'>) {
  return <p {...props} />
}

const HEADER_TITLE = {
  dialog: DialogTitle,
  alert: AlertDialogTitle,
  plain: PlainTitle,
} as const

const HEADER_DESCRIPTION = {
  dialog: DialogDescription,
  alert: AlertDialogDescription,
  plain: PlainDescription,
} as const

export function BrewDialogHeader({
  kicker,
  title,
  description,
  medal,
  tone = 'default',
  trailing,
  kind = 'dialog',
  titleId,
  descriptionId,
  className,
}: {
  /**
   * `alert` inside `BrewAlertDialogContent` (Radix AlertDialog titles);
   * `plain` for a portaled modal outside Radix (pass `titleId`).
   */
  kind?: 'dialog' | 'alert' | 'plain'
  /** Ids for a `plain` header, for the modal's aria-labelledby/-describedby. */
  titleId?: string
  descriptionId?: string
  kicker?: ReactNode
  title: ReactNode
  description?: ReactNode
  medal?: ReactNode
  tone?: BrewDialogTone
  /** Inline next to the title (a status badge). */
  trailing?: ReactNode
  className?: string
}) {
  const { size, hasCloseButton } = useContext(FrameContext)
  const inset = size === 'sm' ? 'px-6' : 'px-7'
  const Title = HEADER_TITLE[kind]
  const Description = HEADER_DESCRIPTION[kind]
  return (
    <div
      data-slot="brew-dialog-header"
      className={cn(
        'flex shrink-0 flex-col gap-2 pt-6 text-left',
        inset,
        // Room for the floating close X only when there is one.
        hasCloseButton && 'pr-16',
        className
      )}
    >
      {medal}
      {kicker ? (
        <div
          className={cn(
            'font-semibold text-caption uppercase leading-[14px] tracking-dialog-kicker',
            tone === 'danger'
              ? 'text-[var(--dlg-danger-ink)]'
              : 'text-[var(--dlg-accent-deep)]'
          )}
        >
          {kicker}
        </div>
      ) : null}
      <div className="flex min-w-0 items-center gap-2">
        <Title
          // Spread only when set: an explicit undefined id would replace the
          // id Radix assigns for the dialog's aria-labelledby.
          {...(titleId ? { id: titleId } : {})}
          className={cn(
            'min-w-0 text-balance font-semibold text-[var(--dlg-ink)] tracking-[-0.02em] [font-family:system-ui,-apple-system,"Segoe_UI",Roboto,sans-serif]',
            size === 'sm'
              ? 'text-xl leading-[26px]'
              : 'text-dialog-title leading-7'
          )}
        >
          {title}
        </Title>
        {trailing}
      </div>
      {description ? (
        <Description
          {...(descriptionId ? { id: descriptionId } : {})}
          className="max-w-[62ch] text-pretty text-[var(--dlg-body)] text-sm leading-[22px]"
        >
          {description}
        </Description>
      ) : null}
    </div>
  )
}

export function BrewDialogBody({ className, ...props }: ComponentProps<'div'>) {
  const inset = useBrewDialogInset()
  return (
    <div
      data-slot="brew-dialog-body"
      className={cn(
        // pb-1 keeps the last card's 1px ring inside the scroll clip.
        'flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto overscroll-contain pt-5 pb-1',
        inset,
        className
      )}
      {...props}
    />
  )
}

/** A strip between header and body (a search field, a filter row). */
export function BrewDialogToolbar({
  className,
  ...props
}: ComponentProps<'div'>) {
  const inset = useBrewDialogInset()
  return (
    <div
      data-slot="brew-dialog-toolbar"
      className={cn('shrink-0 pt-4', inset, className)}
      {...props}
    />
  )
}

export function BrewDialogFooter({
  start,
  className,
  children,
  ...props
}: ComponentProps<'div'> & {
  /** Left slot: a caption, a counter, or one quiet secondary action. */
  start?: ReactNode
}) {
  const inset = useBrewDialogInset()
  return (
    <div
      data-slot="brew-dialog-footer"
      className={cn(
        'flex shrink-0 flex-col-reverse gap-3 pt-5 pb-7 sm:flex-row sm:items-center',
        start ? 'sm:justify-between' : 'sm:justify-end',
        inset,
        className
      )}
      {...props}
    >
      {start ? (
        <div className="min-w-0 text-[var(--dlg-muted)] text-dialog-meta">
          {start}
        </div>
      ) : null}
      <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:items-center">
        {children}
      </div>
    </div>
  )
}

/** Muted body note in the dialog's own ink scale. */
export function BrewDialogNote({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      className={cn(
        'text-[var(--dlg-body)] text-dialog-meta leading-5',
        className
      )}
      {...props}
    />
  )
}
